import { Directive, ElementRef, inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import {
  onAudioPlayerInitialized,
  onPluginReady,
  onVideoPlayerInitialized,
  onVideoPlayerLoadedData,
} from '../services/annotation.service';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { SubsectionService } from '../services/subsection.service';
import { EmitterService } from '../services/emitter.service';

interface Dimensions {
  height: number;
  windowWidth: number;
  elementBoundingClientRect: DOMRect;
}

@Directive({
  selector: '[resizeObserverElement]',
  standalone: false,
})
export class ResizeObserverElementDirective implements OnInit, OnChanges, OnDestroy {
  private readonly subsectionService = inject(SubsectionService);
  private readonly RESIZE_DEBOUNCE = 250;
  private readonly UPDATE_CANVAS_DEBOUNCE = 500;
  private readonly VIDEO_ASPECT_RATIO = 16 / 9;
  private readonly AUDIO_HEIGHT_OFFSET = 65;
  private readonly ngUnsubscribe = new Subject<void>();
  private bodyResize?: ResizeObserver;
  private nativeElement?: NodeListOf<Element>;
  private parentTimeoutId?: number;
  private childTimeoutId?: number;
  private canvasResizeingTimeout?: number;
  private initHeight = true;

  @Input() enableSizeObserver = false;
  @Input() parentElementContainer: string;
  @Input() element!: string;
  @Input() bottomGap = 8;
  @Input() isVideoJs = false;
  @Input() isImage = false;
  @Input() isAudio = false;
  @Input() minHeight = 100;
  @Input() resizeObserverInstanceId?: string;

  private _parentElement: HTMLElement;

  constructor(private readonly el: ElementRef) {}

  ngOnInit(): void {
    if (!this.enableSizeObserver) return;
    if (this.parentElementContainer) {
      const parentElement = document.querySelector(this.parentElementContainer);
      this._parentElement = parentElement ? parentElement : this.el.nativeElement.closest(this.parentElementContainer);
    }
    this.el.nativeElement.classList.add('resize-observer-element');
    this.bodyResize = new ResizeObserver(() => this.stateUpdate());
    this.setInitialHeight();
    this.initializeEvents();
    this.bodyResize.observe(document.body);
    EmitterService.get('RESIZE_OBSERVER_STATE_UPDATE')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((payload: any) => {
        if (
          payload?.instanceId &&
          this.resizeObserverInstanceId &&
          payload.instanceId !== this.resizeObserverInstanceId
        ) {
          return;
        }
        this.stateUpdate();
      });
    EmitterService.get('MEDIA_PLAYER:FULLSCREEN')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          if (this.enableSizeObserver) {
            this.stateUpdate();
          } else {
            this.el.nativeElement.classList.remove('element-resizing');
          }
        },
      });
  }

  ngOnChanges(): void {
    if (this.enableSizeObserver) {
      this.stateUpdate();
    } else {
      this.el.nativeElement.classList.remove('element-resizing');
    }
  }

  ngOnDestroy(): void {
    this.bodyResize?.disconnect();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    window.clearTimeout(this.parentTimeoutId);
    window.clearTimeout(this.childTimeoutId);
    window.clearTimeout(this.canvasResizeingTimeout);
  }

  private setInitialHeight(): void {
    this.setContainerHeight();
    if (!this.isImage && !this.isAudio) return;

    this.nativeElement = this.el.nativeElement.querySelectorAll(this.element);
    if (!this.nativeElement?.length) return;

    const dimensions = this.calculateDimensions();
    const parentHeight = this._parentElement ? this._parentElement.getBoundingClientRect().height : window.innerHeight;
    const calculatedHeight = Math.max(
      dimensions.height + (this.subsectionService.uiState.commentsCollapsed ? this.bottomGap : 0),
      this.minHeight,
      parentHeight - dimensions.elementBoundingClientRect.top - this.bottomGap
    );
    const height = this.isAudio ? calculatedHeight + this.AUDIO_HEIGHT_OFFSET : calculatedHeight;
    this.setElementHeight(this.nativeElement[0], height);

    if (this.isAudio) {
      this.updateAudioSpacers();
    }
  }

  private setContainerHeight() {
    //this.el.nativeElement.style.height = `calc(100vh - ${this.el.nativeElement.getBoundingClientRect().top + this.bottomGap}px)`;
  }

  private initializeEvents(): void {
    this.initializeStateSubscriptions();
    this.initializeNavSubscription();
    this.initializeMediaSubscriptions();
  }

  private initializeStateSubscriptions(): void {
    const { uiState$ } = this.subsectionService;

    uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.explorerCollapsed === curr.explorerCollapsed)
      )
      .subscribe(() => this.stateUpdate());

    uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.commentsCollapsed === curr.commentsCollapsed)
      )
      .subscribe(() => this.stateUpdate());
  }

  private initializeNavSubscription(): void {
    EmitterService.get('NAV:EXPANDED')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.stateUpdate());
  }

  private initializeMediaSubscriptions(): void {
    if (this.isVideoJs) {
      this.initializeVideoSubscription();
    } else if (this.isImage) {
      this.initializeImageSubscription();
    } else if (this.isAudio) {
      this.initializeAudioSubscription();
    } else {
      this.initializeDefaultElement();
    }
  }

  private initializeVideoSubscription(): void {
    onVideoPlayerLoadedData.pipe(takeUntil(this.ngUnsubscribe)).subscribe((args) => {
      this.el.nativeElement.classList.add('element-initialized');
      this.setupVideoElement(args);
    });
  }

  private initializeImageSubscription(): void {
    onPluginReady.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.el.nativeElement.classList.add('element-initialized');
      this.setupMediaElement();
    });
  }

  private initializeAudioSubscription(): void {
    onAudioPlayerInitialized.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.el.nativeElement.classList.add('element-initialized');
      this.setupAudioElement();
    });
  }

  private setupVideoElement(args): void {
    this.nativeElement = this.el.nativeElement.querySelectorAll(this.element);
    if (!this.nativeElement?.length) return;

    this.bottomGap = this.subsectionService.uiState.commentsCollapsed ? 10 : 76;
    this.stateUpdate();
  }

  private setupMediaElement(): void {
    this.nativeElement = this.el.nativeElement.querySelectorAll(this.element);
    if (this.nativeElement?.length) {
      this.stateUpdate();
    }
  }

  private setupAudioElement(): void {
    this.nativeElement = this.el.nativeElement.querySelectorAll(this.element);
    if (!this.nativeElement?.length) return;

    this.bottomGap = this.subsectionService.uiState.commentsCollapsed ? 72 : 144;
    this.stateUpdate();
  }

  private initializeDefaultElement(): void {
    this.nativeElement = this.el.nativeElement.querySelectorAll(this.element);
    if (this.nativeElement?.length) {
      this.stateUpdate();
    }
  }

  private stateUpdate(): void {
    if (!this.nativeElement?.length || !this.enableSizeObserver) return;

    this.el.nativeElement.classList.add('element-resizing');
    window.clearTimeout(this.parentTimeoutId);
    window.clearTimeout(this.childTimeoutId);
    window.clearTimeout(this.canvasResizeingTimeout);

    this.parentTimeoutId = window.setTimeout(() => {
      requestAnimationFrame(() => {
        const dimensions = this.calculateDimensions();

        if (this.isVideoJs) {
          this.updateVideoElement(dimensions);
        } else if (this.isImage || this.isAudio) {
          this.updateMediaElement(dimensions);
        } else {
          this.updateDefaultElement(dimensions);
        }
      });
    }, this.RESIZE_DEBOUNCE);
  }

  private calculateDimensions(): Dimensions {
    const parentHeight = this._parentElement ? this._parentElement.getBoundingClientRect().height : window.innerHeight;
    const parentWidth = this._parentElement ? this._parentElement.getBoundingClientRect().width : window.innerWidth;
    const windowHeight = parentHeight;
    const elementBoundingClientRect = this.nativeElement![0].getBoundingClientRect();
    const height = this.calculateHeight(windowHeight, elementBoundingClientRect);

    return { height, windowWidth: parentWidth, elementBoundingClientRect };
  }

  private calculateHeight(windowHeight: number, elementBoundingClientRect: DOMRect): number {
    const baseHeight = windowHeight - elementBoundingClientRect.top - this.bottomGap;

    if (this.isAudio || this.isImage || this.isVideoJs) {
      return Math.min(windowHeight - this.bottomGap, baseHeight);
    }

    return Math.max(windowHeight - elementBoundingClientRect.height, baseHeight);
  }

  private updateVideoElement(dimensions: Dimensions): void {
    const videoElementContainer = this.el.nativeElement.querySelectorAll('#videoJsEl'),
      videoElement = videoElementContainer[0].querySelectorAll('.vjs-tech');
    if (!videoElementContainer?.length && !videoElement?.length) return;

    const videoHeight = this.calculateVideoHeight(dimensions);
    const videoWidth = videoHeight * this.VIDEO_ASPECT_RATIO;

    // this.setVideoElementContainerStyle(videoElementContainer[0], videoHeight, videoWidth);
    // this.setVideoElementStyle(videoElement[0], videoHeight, videoWidth);
    this.updateVideoCanvas(videoHeight, videoWidth);

    if (!this.initHeight || !this.subsectionService.uiState.commentsCollapsed) {
      this.updateVideoToolbar();
    }
    this.initHeight = false;
    this.el.nativeElement.classList.add('element-initialized');
  }

  private calculateVideoHeight(dimensions: Dimensions): number {
    const parentHeight = this._parentElement ? this._parentElement.getBoundingClientRect().height : window.innerHeight;
    const maxHeight = Math.min(
      dimensions.height,
      ((dimensions.windowWidth - dimensions.elementBoundingClientRect.left - 16) * 9) / 16,
      parentHeight - dimensions.elementBoundingClientRect.top - this.bottomGap
    );
    return Math.max(maxHeight, this.minHeight);
  }

  // private setVideoElementContainerStyle(element: any, height: number, width: number): void {
  //   element.style.paddingTop = `${height}px`;
  //   element.style.width = '100%';
  //   element.style.transition = 'all 0.25s ease';
  // }

  // private setVideoElementStyle(element: any, height: number, width: number): void {
  //   element.style.width = `${width}px`;
  //   element.style.left = '50%';
  //   element.style.height = `${height - 42}px`;
  //   element.style.transform = 'translateX(-50%)';
  //   element.style.transition = 'height 0.25s ease';
  // }

  private updateVideoCanvas(height: number, width: number) {
    if (!this.enableSizeObserver) {
      clearTimeout(this.canvasResizeingTimeout);
      this.el.nativeElement.classList.remove('element-resizing');
      return;
    }
    const vacCanvas = this.el.nativeElement.querySelectorAll('.vac-canvas');
    const canvasContainer = this.el.nativeElement.querySelectorAll('.vac-canvas .canvas-container');
    const canvas = this.el.nativeElement.querySelectorAll('.vac-canvas .canvas-container canvas');
    const upperCanvas = this.el.nativeElement.querySelectorAll('.vac-canvas .canvas-container .upper-canvas');
    if (!vacCanvas?.length || !canvasContainer?.length || !canvas?.length || !upperCanvas?.length) return;

    this.canvasResizeingTimeout = window.setTimeout(() => {
      const videoElement = document.querySelector('.vjs-tech') as HTMLVideoElement;
      if (!videoElement) return;

      const containerDimensions = this.calculateCanvasDimensions(width, height);

      const canvasStyle = `
        height: ${containerDimensions.height}px;
        position: absolute;
        width: ${containerDimensions.width}px;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      `;

      vacCanvas[0].style.cssText = canvasStyle;
      canvasContainer[0].style.cssText = canvasStyle;
      canvas[0].style.cssText = canvasStyle;
      upperCanvas[0].style.cssText = canvasStyle;
      this.el.nativeElement.classList.remove('element-resizing');
    }, this.UPDATE_CANVAS_DEBOUNCE);
  }

  private updateVideoToolbar(): void {
    const videoToolbar: any = this.el.nativeElement.querySelectorAll('.video-js-element-container .vjs-control-bar');
    if (!videoToolbar?.length) return;

    this.childTimeoutId = window.setTimeout(() => {
      videoToolbar[0].style.position = 'absolute';
    }, this.RESIZE_DEBOUNCE);
  }

  private updateMediaElement(dimensions: Dimensions): void {
    const parentHeight = this._parentElement ? this._parentElement.getBoundingClientRect().height : window.innerHeight;
    const calculatedHeight = Math.max(
      dimensions.height,
      this.minHeight,
      parentHeight - dimensions.elementBoundingClientRect.top - this.bottomGap
    );
    const height = this.isAudio ? calculatedHeight + this.AUDIO_HEIGHT_OFFSET : calculatedHeight;

    if (this.isAudio) {
      this.updateAudioSpacers(height);
    }

    if (!this.initHeight) {
      this.setElementHeight(this.nativeElement![0], height);
    }
    this.initHeight = false;
    this.el.nativeElement.classList.add('element-initialized');
  }

  private updateDefaultElement(dimensions: Dimensions): void {
    const parentHeight = this._parentElement ? this._parentElement.getBoundingClientRect().height : window.innerHeight;
    const height = Math.max(
      dimensions.height,
      this.minHeight,
      parentHeight - dimensions.elementBoundingClientRect.top - this.bottomGap
    );
    this.setElementHeight(this.nativeElement![0], height);
  }

  private updateAudioSpacers(height?: number): void {
    const colorSpacerTop: any = this.el.nativeElement.querySelectorAll('.colour-spacer-top');
    const colorSpacerBottom: any = this.el.nativeElement.querySelectorAll('.colour-spacer-bottom');

    if (!colorSpacerTop?.length || !colorSpacerBottom?.length) return;

    const parentHeight = this._parentElement ? this._parentElement.getBoundingClientRect().height : window.innerHeight;
    const spacerHeight = height ? (height - 200) / 2 : (parentHeight - 200) / 2;
    const adjustment = this.initHeight ? 10 : 0;

    colorSpacerTop[0].style.height = `${spacerHeight + adjustment}px`;
    colorSpacerBottom[0].style.height = `${spacerHeight + adjustment}px`;
  }

  private setElementHeight(element: any, height: number): void {
    element.style.height = `${height}px`;
    element.style.transition = 'height 0.25s ease';
  }

  private calculateCanvasDimensions(
    containerWidth: number,
    containerHeight: number
  ): { width: number; height: number } {
    const videoElement = this.el.nativeElement.querySelector('.vjs-tech') as HTMLVideoElement;

    if (!videoElement) {
      return { width: containerWidth, height: containerHeight };
    }

    const displayHeight = videoElement.offsetHeight;
    const displayWidth = videoElement.offsetWidth;
    const nativeWidth = videoElement.videoWidth;
    const nativeHeight = videoElement.videoHeight;

    const containerAspectRatio = displayWidth / displayHeight;
    const videoAspectRatio = nativeWidth / nativeHeight;

    let scaleFactor;
    if (containerAspectRatio > videoAspectRatio) {
      scaleFactor = displayHeight / nativeHeight;
    } else {
      scaleFactor = displayWidth / nativeWidth;
    }

    const finalWidth = Math.round(nativeWidth * scaleFactor);
    const finalHeight = Math.round(nativeHeight * scaleFactor);

    return {
      width: finalWidth,
      height: finalHeight,
    };
  }
}
